import {IProductModel, IProducts, ProductProtocol} from '@/modules/products/IProductModel';
import {BaseModel} from '@/core/standard/BaseModel';
import ProductService from '@/modules/products/ProductService';
import {IModifierModel} from '@/modules/modifiers/IModifierModel';

const product = {
  name: '',
  name_es: '',
  name_en: '',
  price: null,
  purchase_price: null,
  category: '',
  allergens: '',
  ingredients: [],
  description: '',
  modifiers: [],
  description_es: '',
  description_en: '',
  image: '',
  stk_uuid: '',
  serial: '',
  is_active: true,
  qty: 1,
  qty_existence: '',
  unit: '',
  unit_value: 1,
  min_stock: null,
  order_number: '',
  order: null,
  zones: [],
  units_list: [],
  iva: 0,
};

class ProductModel extends BaseModel<IProductModel, ProductProtocol> {
  public modelName = 'Producto';
  public request = 'FormData';

  constructor() {
    super(product, ProductService);
  }

  public async mapRow(row: IProductModel & { id: string | number }) {
    const fetchedRow = await this.service.getBy({query: {}, id: row.id});
    const {complete, data} = fetchedRow;
    if (complete) {
      super.mapRow(data);
    }
  }

  public async getProduct(data) { 
    const response = await this.service.getProduct(data);
    return response.data;
  }

  public async fetchRecipes(protocol: ProductProtocol): Promise<any> {
    const response = await this.service.getAllRecipes({method: 'GET', query: protocol});
    if (response && response.data) {
      return {
        data: response.data.results,
        total: response.data.total,
      };
    }
    return {data: [], total: 0};
  }

  public async save(toast, toastConfig): Promise<any> {
    // @ts-ignore
    const formData = new FormData();
    Object.keys(product).forEach((field) => {
      if (this[field] !== null && this[field] !== undefined) {
        if (typeof this[field] === 'object' && !(this[field] instanceof File)) {
          formData.append(field, JSON.stringify(this[field]));
        } else {
          const splittedField = field.split('_');
          const lang = localStorage.getItem('lang') || 'es';
          if (splittedField[splittedField.length - 1] !== lang) {
            formData.append(field, this[field]);
          }
        }
      }
    });
    // @ts-ignore
    if (this.ingredients.length) {
      // @ts-ignore
      const newIng = this.ingredients.map(({id, qty, unit}) => {
        return {id, qty, unit};
      });
      formData.set('ingredients', JSON.stringify(newIng.filter((ing) => !!ing)));
    }
    // @ts-ignore
    if (!this.min_stock) {
      formData.set('min_stock', '0');
    }
    if (!formData.get('image')) {
      if (!formData.has('image')) {
        formData.append('image', '');
      }
    } else if (formData.has('image') && !(formData.get('image') instanceof File)) {
      formData.delete('image');
    }
    if (formData.has('modifiers')) {
      // @ts-ignore
      const modifiers = JSON.parse(formData.get('modifiers') || '[]');
      const newModifier = this.mapRecursively(modifiers);
      formData.set('modifiers', JSON.stringify(newModifier));
    }
    formData.delete('stk_uuid');
    // @ts-ignore
    if (this.zones.length) {
      const filledZones = ((this as any).zones as Array<Record<any, any>>).filter((zone) => zone.zone && zone.price);
      formData.set('zones',
        JSON.stringify(filledZones));
    }
    // @ts-ignore
    if (this.id) {
      // @ts-ignore
      formData.append('id', this.id);
      return this.update(formData, toast, toastConfig);
    } else {
      return this.create(formData, toast, toastConfig);
    }
  }

  public mapRecursively(modifiers) {
    return (modifiers as IModifierModel[]).map((modifier) => ({
      id: modifier.id,
      enabled: modifier.enabled,
      min_value: modifier.min_value,
      max_value: modifier.max_value,
      options: modifier.options.map((option) => ({
        id: option.id,
        price: option.price || '0.00',
        enabled: option.enabled,
        min_value: option.min_value || 1,
        max_value: option.max_value || 9,
        ingredients: option.ingredients || [],
        modifiers: this.mapRecursively(option.modifiers),
      })),
    }));
  }

  public async getProductOrder(id) {
    const fetchedRow = await this.service.getProductOrder({id});
    const {complete, data} = fetchedRow;
    if (complete) {
      return data;
    }
    return false;
  }

  public async changeCategories(productsIds, categoryId) {
    let query = {
      pk__in: productsIds,
      category_id: categoryId
    };
    return await this.service.changeCategories({query});
  }

  public async getProductsListByLocal(protocol: ProductProtocol): Promise<any> {
    const response = await this.service.getProductsListByLocal({method: 'GET', ...protocol});
    if (response && response.data) {
      return {
        data: response.data.results,
        total: response.data.total,
      };
    }
    return {data: [], total: 0};
  }
}

export default ProductModel as new () => IProducts;
